import { normalizePathAPeriods, normalizePathBPeriods } from "@/views/mobile/wizard/commons/periods"
import { isEqual } from "lodash"

const normalizePeriodSet = periods => (Array.isArray(periods) ? [...new Set(periods)].sort((left, right) => left - right) : periods)

export default {
  namespaced: true,
  state: () => ({
    highlightChangesEnabled: true, // feature flag

    // Path B
    hasHighlightedBasicInput: false,
    // monthly result charts state flags
    hasHighlightedEnergyConsumption: false,
    hasHighlightedAveragePower: false,
    hasHighlightedChillerCount: false,

    // Path A should have a separate set of flags
    // path A contain unique basic chart(eg operating hours, hourly chiller operation), so reusing path B's hasHighlightedBasicInput would cause error
    hasHighlightedBasicInputPathA: false,
    // path A calc logic: basic -> monthly cooling load -> monthly chiller count -> monthly average power
    // so have a new set of flags are reasonable to reflect diff calc order
    hasHighlightedCoolingLoadPathA: false,
    hasHighlightedChillerCountPathA: false
  }),
  getters: {
    hasPendingResultChanges: (state, getters, rootState) => {
      const pre = rootState.projectPre?.currentProject?.pre
      const { estimatePath, pendingResultChanges } = pre?.profileCfg || {}
      if (![1, 2].includes(estimatePath)) return false
      const profileChanges = pendingResultChanges?.[estimatePath === 1 ? "pathA" : "pathB"]
      return (pre?.profiles || []).some(profile => profileChanges === true || profileChanges?.[profile.id] === true)
    },
    reviewChanges: (state, getters, rootState) => rootState.projectPre?.currentProject?.reviewChanges || {},
    initChanges: (state, getters, rootState) => rootState.projectPre?.currentProject?.initChanges || {},
    hasGeneratedDefaults: (state, getters, rootState) => (rootState.projectPre?.currentProject?.step ?? 0) >= 1,
    hasGeneratedReport: (state, getters, rootState) => (rootState.projectPre?.currentProject?.step ?? 0) >= 2
  },
  mutations: {
    STORE_HIGHLIGHTED_BASIC_INPUT(state, hasHighlights) {
      state.hasHighlightedBasicInput = hasHighlights
    },
    SET_HIGHLIGHT_CHANGES_ENABLED(state, enabled) {
      state.highlightChangesEnabled = enabled
    },
    STORE_HIGHLIGHTED_ENERGY_CONSUMPTION(state, hasHighlights) {
      state.hasHighlightedEnergyConsumption = hasHighlights
    },
    STORE_HIGHLIGHTED_AVERAGE_POWER(state, hasHighlights) {
      state.hasHighlightedAveragePower = hasHighlights
    },
    STORE_HIGHLIGHTED_CHILLER_COUNT(state, hasHighlights) {
      state.hasHighlightedChillerCount = hasHighlights
    },
    STORE_HIGHLIGHTED_BASIC_INPUT_PATH_A(state, hasHighlights) {
      state.hasHighlightedBasicInputPathA = hasHighlights
    },
    STORE_HIGHLIGHTED_CHILLER_COUNT_PATH_A(state, hasHighlights) {
      state.hasHighlightedChillerCountPathA = hasHighlights
    },
    STORE_HIGHLIGHTED_COOLING_LOAD_PATH_A(state, hasHighlights) {
      state.hasHighlightedCoolingLoadPathA = hasHighlights
    }
  },
  actions: {
    cachePendingResultChanges({ rootState, commit }, { path, profileId, hasHighlights }) {
      const pre = rootState.projectPre?.currentProject?.pre
      if (!pre || ![1, 2].includes(path) || profileId == null || !pre.profiles?.some(profile => profile.id === profileId)) return
      const pathKey = path === 1 ? "pathA" : "pathB"
      const existing = pre.profileCfg?.pendingResultChanges?.[pathKey]
      const profileChanges = typeof existing === "boolean" ? Object.fromEntries(pre.profiles.map(profile => [profile.id, existing])) : existing || {}
      if (typeof existing !== "boolean" && profileChanges[profileId] === hasHighlights) return
      const pendingResultChanges = { pathA: {}, pathB: {}, ...pre.profileCfg?.pendingResultChanges, [pathKey]: { ...profileChanges, [profileId]: hasHighlights } }
      commit("projectPre/STORE_PROJ_PRE", { profileCfg: { ...pre.profileCfg, pendingResultChanges } }, { root: true })
    },
    storeEnabledPeriods({ rootState, commit }, enabledPeriods) {
      const pre = rootState.projectPre?.currentProject?.pre
      if (!pre) return
      const updated = { ...pre, profileCfg: { ...pre.profileCfg, enabledPeriods } }
      const previousPeriods = pre.profileCfg?.enabledPeriods || {}
      const periodSettingsChanged = ["weekday", "weekend", "wholeWeek"].some(day => !isEqual(normalizePeriodSet(previousPeriods[day]), normalizePeriodSet(enabledPeriods[day])))
      const normalized = periodSettingsChanged ? (updated.profileCfg.estimatePath === 2 ? normalizePathBPeriods(updated) : normalizePathAPeriods(updated)) : updated
      commit("projectPre/STORE_PROJ_PRE", normalized, { root: true })
    },
    storeReviewChanges({ commit }, reviewChanges) {
      commit("projectPre/STORE_REVIEW_CHANGES", reviewChanges, { root: true })
    },
    storeHighlightedBasicInput({ commit }, hasHighlights) {
      commit("STORE_HIGHLIGHTED_BASIC_INPUT", hasHighlights)
    },
    setHighlightChangesEnabled({ commit }, enabled) {
      commit("SET_HIGHLIGHT_CHANGES_ENABLED", enabled)
    },
    storeHighlightedEnergyConsumption({ commit }, hasHighlights) {
      commit("STORE_HIGHLIGHTED_ENERGY_CONSUMPTION", hasHighlights)
    },
    storeHighlightedAveragePower({ commit }, hasHighlights) {
      commit("STORE_HIGHLIGHTED_AVERAGE_POWER", hasHighlights)
    },
    storeHighlightedChillerCount({ commit }, hasHighlights) {
      commit("STORE_HIGHLIGHTED_CHILLER_COUNT", hasHighlights)
    },
    storeHighlightedBasicInputPathA({ commit }, hasHighlights) {
      commit("STORE_HIGHLIGHTED_BASIC_INPUT_PATH_A", hasHighlights)
    },
    storeHighlightedCoolingLoadPathA({ commit }, hasHighlights) {
      commit("STORE_HIGHLIGHTED_COOLING_LOAD_PATH_A", hasHighlights)
    },
    storeHighlightedChillerCountPathA({ commit }, hasHighlights) {
      commit("STORE_HIGHLIGHTED_CHILLER_COUNT_PATH_A", hasHighlights)
    }
  }
}



// WEBPACK FOOTER //
// ./src/store/modules/wizard.js